// La mitad "de browser" de los adjuntos: `File` → `AdjuntoChat`.
//
// Se separa de `adjuntosChatAtlas.ts` (que es puro y testeado) porque acá hace
// falta FileReader, canvas y los conversores de formato — nada de eso corre en
// el entorno de tests, que es Node sin DOM.
//
// Toda imagen pasa por `normalizeImageForUpload` ANTES que nada: es el único
// lugar de la app donde se decide qué formatos se aceptan y cuáles se
// convierten (HEIC/AVIF/WEBP → JPEG; RAW/TIFF/GIF/SVG rechazados con el
// mensaje común). Saltearlo acá reestrenaría el bug de "subí una foto del
// iPhone y no pasa nada".

import { normalizeImageForUpload } from '../../utils/heicConvert';
import { resizeImageIfNeeded } from '../../utils/imageResize';
import {
  claseDeArchivo,
  LADO_MAX_IMAGEN,
  MAX_BYTES_IMAGEN,
  MAX_CHARS_TEXTO,
  mediaTypeImagen,
  MENSAJE_FORMATO_ADJUNTO,
  MENSAJE_IMAGEN_PESADA,
} from '../adjuntosChatAtlas';
import type { AdjuntoChat } from '../tipos';

const nuevoId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `adj-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
};

/** base64 SIN el prefijo `data:...;base64,`. */
const leerBase64 = (archivo: Blob): Promise<string> =>
  new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No pude leer el archivo.'));
    lector.onload = () => {
      const crudo = String(lector.result || '');
      const coma = crudo.indexOf(',');
      resolver(coma >= 0 ? crudo.slice(coma + 1) : '');
    };
    lector.readAsDataURL(archivo);
  });

const leerTexto = (archivo: Blob): Promise<string> =>
  new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No pude leer el archivo.'));
    lector.onload = () => resolver(String(lector.result || ''));
    lector.readAsText(archivo);
  });

/**
 * Convierte un archivo elegido por el usuario en un adjunto listo para viajar.
 *
 * Tira `Error` con un mensaje ya redactado para mostrar tal cual: los
 * llamadores no tienen que traducir nada.
 */
export async function prepararAdjunto(archivo: File): Promise<AdjuntoChat> {
  const clase = claseDeArchivo(archivo.name, archivo.type);
  if (!clase) throw new Error(MENSAJE_FORMATO_ADJUNTO);

  if (clase === 'texto') {
    const crudo = await leerTexto(archivo);
    const recortado = crudo.length > MAX_CHARS_TEXTO;
    const datos = recortado
      ? `${crudo.slice(0, MAX_CHARS_TEXTO)}\n\n[…recortado: el archivo seguía, pero solo entra este pedazo.]`
      : crudo;
    if (!datos.trim()) throw new Error('Ese archivo está vacío.');
    return {
      id: nuevoId(),
      nombre: archivo.name,
      clase: 'texto',
      mediaType: 'text/plain',
      datos,
      bytes: datos.length,
    };
  }

  // Imagen: normalizar formato → achicar → base64.
  const normalizada = await normalizeImageForUpload(archivo);
  const achicada = await resizeImageIfNeeded(normalizada, LADO_MAX_IMAGEN, 0.8);
  const datos = await leerBase64(achicada);
  if (!datos) throw new Error('No pude leer esa imagen.');
  // El peso que importa es el del contenido convertido, no el del archivo
  // original: es lo que se manda y lo que cuenta contra el límite del body.
  const bytes = Math.round((datos.length * 3) / 4);
  if (bytes > MAX_BYTES_IMAGEN) throw new Error(MENSAJE_IMAGEN_PESADA);

  return {
    id: nuevoId(),
    nombre: archivo.name,
    clase: 'imagen',
    mediaType: mediaTypeImagen(achicada.type || normalizada.type),
    datos,
    bytes,
  };
}

/** `data:` URL para dibujar la miniatura de un adjunto de imagen.
 *
 *  Se arma del base64 que ya tenemos en memoria en vez de un `objectURL`: no
 *  hay que revocarlo y no pasa por red — así que tampoco corresponde
 *  `CachedImg`, que existe para URLs REMOTAS.
 *
 *  OJO: sólo sirve mientras el adjunto tenga `datos`. El caché de sesión los
 *  guarda vacíos a propósito (ver `mensajesParaCache`), así que después de un
 *  refresh `BurbujaMensaje` dibuja la ficha del archivo en vez de la
 *  miniatura. */
export const urlDeAdjunto = (adjunto: AdjuntoChat): string =>
  `data:${mediaTypeImagen(adjunto.mediaType)};base64,${adjunto.datos}`;
